import { useState } from 'react';
import type { FeatureCategory as FeatureCategoryType } from '@/content/features';

interface FeatureCategoryProps {
  category: FeatureCategoryType;
  defaultOpen?: boolean;
}

/**
 * FeatureCategory Component
 * Accordion-style deep dive into feature categories
 * Each category can be expanded to show detailed bullet points
 */
export default function FeatureCategory({ category, defaultOpen = false }: FeatureCategoryProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-2xl border border-secondary-200 dark:border-secondary-700 shadow-soft overflow-hidden">
      {/* Header - Clickable to toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
        aria-expanded={isOpen}
        aria-controls={`category-${category.id}`}
      >
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-secondary-50 mb-1">
            {category.title}
          </h3>
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            {category.description}
          </p>
        </div>

        {/* Chevron icon */}
        <svg
          className={`w-6 h-6 text-secondary-500 transition-transform duration-200 ml-4 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Content - Expandable */}
      <div
        id={`category-${category.id}`}
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div className="px-6 pb-5 pt-2 border-t border-secondary-200 dark:border-secondary-700">
          <ul className="space-y-3">
            {category.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                {/* Bullet point */}
                <span className="flex-shrink-0 w-1.5 h-1.5 bg-primary-500 rounded-full mt-2" />
                
                {/* Feature text */}
                <span className="text-secondary-700 dark:text-secondary-300 leading-relaxed">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

